// TODO: DASH - improve this pattern - it must be possible to re-use common logic for tradeIn, incentives and trade-in.
// Maybe we could actually do something in dr-activities-common for this and/or expose this type of middleware directly from the
// feature package (if that makes sense?)

// externals
import { Middleware, MiddlewareAPI } from 'redux';

// libraries
import { urlBuilder } from '@makemydeal/dr-common-utils';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
// TODO: DASH - update trade-in activity to use MiddlewareManager naming (or Middleware Factory?)
import { Middleware as MiddlewareManager, Actions } from '@makemydeal/dr-activities-tradein';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors, historyInstance, initActionTypes } from '@makemydeal/dr-shared-store';
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';
import * as tradeShopperActivity from '@makemydeal/dr-dash-store/shopperActivities/trade';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as tradeInActionCreators from '@makemydeal/dr-dash-store/actions/tradeInActionCreators';
import * as tradeInCardActionCreators from '@makemydeal/dr-dash-store/actions/tradeInCardActionCreators';
import * as tradeInCardSelectors from '@makemydeal/dr-dash-store/selectors/tradeInCardSelectors';
import * as tradeActionTypes from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';
import * as tradesActionTypes from '@makemydeal/dr-dash-store/actionTypes/tradesActionTypes';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import { clientLogger, MANAGER_VIEW_APP_PREFIX, payloadUtils } from '@makemydeal/dr-shared-ui-utils';

// utils
import { relayExternalTrackPayload } from '../../common/services/analyticsRelay';

// interfaces/types
import type { ServiceEndpointConfig } from '../../types/serviceEndpointTypes';

// config
import { configureTradeIn } from './config';
import { getCurrentFlowName } from '../../flowMasterInterfaces';

// components
import { InitConfiguration } from '@makemydeal/dr-dash-bff-types';
import { isVinValid } from '@interstate/components/Validation';

// keep a cached version of the middleware once we're able to build it.
// exported to support testing
export let cachedTradeInMiddlewareMgr: MiddlewareManager | undefined;

// needed for testing
export const tradeInMiddlewareClearCache = () => (cachedTradeInMiddlewareMgr = undefined);

const handleImmediateAction = (store: MiddlewareAPI, action: AnyFSA) => {
    switch (action.type) {
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            // get what we can from state.  This typically will be the bff endpoint only since its placed as part of initial state
            const state = store.getState();
            const bff = configSelectors.getServicesBff(state);
            const kbbTradeInApiKey = configSelectors.getServicesKbb(state).tradeInApiKey || '';

            // now we will not have the toggles, static images or the gateway in state
            // so we will have to replace them
            const config = payloadUtils.getConfigFromInitPayload<InitConfiguration>(action.payload);
            const { staticImages, services: { gateway } = {} } = config;

            // we need staticImages to continue
            if (staticImages && gateway) {
                cachedTradeInMiddlewareMgr = configureTradeIn({
                    services: {
                        bff,
                        gateway
                    },
                    staticImages,
                    utils: {
                        buildApiUrlWithServiceEndpoint: ({ service = 'bff', endPoint = 'init' }: ServiceEndpointConfig): string => {
                            if (service !== 'bff') {
                                throw new Error('CMD buildApiUrlWithServiceEndpoint does not support services other than "bff"');
                            }
                            return urlBuilder.buildFromConfig(bff, endPoint);
                        },
                        getCurrentFlowName,
                        getCurrentLocation: () => {
                            return historyInstance.location.pathname;
                        }
                    },
                    // TODO: DASH - this appears to be other "host actions" - should move them under hostActions for consistency.
                    offerActionCreators: {
                        updatedShopper: offerReduxActionCreators.updatedShopper,
                        updatedTradeIn: offerReduxActionCreators.updatedTradeIn
                    },
                    hostActions: {
                        relayTradeInEvents: relayExternalTrackPayload
                    },
                    kbbTradeInApiKey,
                    // NOTE: Since we are not using the old menu, we don't need to provide it to trade activity.
                    renderMenuProducts: /* istanbul ignore next */ () => null
                });
            } else {
                clientLogger.clientWarnMessage('Could not bootstrap the tradeIn activity.');
            }

            break;
        }
    }
};

const handleActions = (store: MiddlewareAPI, action: AnyFSA) => {
    switch (action.type) {
        case tradeActionTypes.TRADE_DETAILS_MANUAL_ENTRY_SAVE: {
            const state = store.getState();

            store.dispatch(tradeInActionCreators.tradeDetailsManualEntrySaveTradeIn(state.tradeIn));

            // Since Deal Scenarios share trade-in info, need to request every scope to recalculate trade-in payment
            store.dispatch(tradeInActionCreators.requestTradeInPaymentUpdate());

            break;
        }

        case tradesActionTypes.TRADE_INS_ACV_CHANGED:
        case tradesActionTypes.TRADE_INS_PAYOFF_CHANGED:
        case tradesActionTypes.TRADE_INS_UPDATE_OWNERSHIP_TYPE:
        case tradesActionTypes.TRADE_INS_ALLOWANCE_CHANGED: {
            const index = action.payload.index || 0;
            const state = store.getState();
            const includesTradeIn = tradeInsSelectors.getShouldIncludeTradeIn(index)(state);
            // If includesTradeIn is false then skip the payment call, as it will be triggered by the includeTradeChanged action
            // If not skipped here, it would cause double calls to the payment endpoint and there could be race condition issues
            const skipPayment = action.meta?.middleware?.skipPayment || !includesTradeIn;

            // Since Deal Scenarios share trade-in info, need to request every scope to recalculate trade-in payment
            store.dispatch(tradeInActionCreators.requestTradeInPaymentUpdate(skipPayment));

            if (!includesTradeIn) {
                store.dispatch(tradeInsActionCreators.includeTradeChanged(true, index));
            }

            break;
        }

        case tradesActionTypes.DELETE_TRADE_IN:
        case tradesActionTypes.UNDO_DELETED_TRADE_IN: {
            store.dispatch(tradeInActionCreators.requestTradeInPaymentUpdate());
            break;
        }

        case tradeActionTypes.ACV_CHANGED:
        case tradeActionTypes.PAYOFF_CHANGED:
        case tradeActionTypes.TRADE_IN_UPDATE_OWNERSHIP_TYPE:
        case tradeActionTypes.ALLOWANCE_CHANGED: {
            const state = store.getState();
            const includesTradeIn = tradeInCardSelectors.getShouldIncludeTradeIn(state);
            const skipPayment = action.meta?.middleware?.skipPayment;

            // Since Deal Scenarios share trade-in info, need to request every scope to recalculate trade-in payment
            store.dispatch(tradeInActionCreators.requestTradeInPaymentUpdate(skipPayment));

            if (!includesTradeIn) {
                store.dispatch(tradeInCardActionCreators.includeTradeChanged(true));
            }

            break;
        }

        case tradeActionTypes.TRADE_IN_PAYMENT_INFO_UPDATE_REQUEST: {
            const state = store.getState();
            const includesTradeIn = tradeInCardSelectors.getShouldIncludeTradeIn(state);
            const skipPayment = action.meta?.middleware?.skipPayment;
            if (includesTradeIn && skipPayment !== true) {
                store.dispatch(tradeInActionCreators.updateTradeInPayment());
            }

            break;
        }

        case tradeActionTypes.TRADE_DETAILS_MANUAL_ENTRY_CANCEL: {
            const state = store.getState();
            store.dispatch(tradeInActionCreators.manualEntryCancelTradeIn(state.rollbackTradeIn));
            break;
        }

        case Actions.TRADE_IN_PERFORM_UPDATE: {
            store.dispatch(tradeShopperActivity.tradeActionCreators.tradeFlowComplete());
            store.dispatch(navigationActionCreators.navigateToDashboard());
            return;
        }

        case initActionTypes.INIT_SUCCESS: {
            const state = store.getState();
            const { vin, year, make, model, trim } = tradeInSelectors.getTradeVehicleInfo(state);
            const requiredFieldsMissing = !year || !make || !model || !trim;

            // Trigger VIN decoding for valid VINs when required trade vehicle fields are missing
            if (vin && isVinValid(vin) && requiredFieldsMissing) {
                store.dispatch(
                    tradeInActionCreators.vinDecodeRSAA(
                        MANAGER_VIEW_APP_PREFIX,
                        vin,
                        featureToggleSelectors.isPreserveCrmTradeVehicleInfoEnabled(state),
                        featureToggleSelectors.isLoadEnrichmentRebaselineEnabled(state)
                    )
                );
            }
            break;
        }
    }
};

export const tradeInMiddleware: Middleware<unknown, StateTree> = (store: MiddlewareAPI) => (next: any) => (action: AnyFSA) => {
    handleImmediateAction(store, action);

    /**
     * We need to prevent the api call made at the end of the trade flow that persists the trade.
     * Trade persistence is a part of Offer Save in CMD, which is not the case in AMD.
     *
     * So we must skip cachedTradeInMiddlewareMgr at the end of this handler to prevent the rest of the trade activity's middleware
     * from being invoked with this action.
     */
    const shouldSkipActivityTradeInMiddleware = action.type === Actions.TRADE_IN_PERFORM_UPDATE;
    if (cachedTradeInMiddlewareMgr && !shouldSkipActivityTradeInMiddleware) {
        cachedTradeInMiddlewareMgr.middleware(store)(next)(action);
    } else {
        next(action);
    }

    handleActions(store, action);
};
